import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import ModuloController from "./modulo_controller"

// Il passo 5 della scheda di verifica (ModuliCampi, File e Divisione di components/verifica/ModuliCampi.js), sul frame
// moduli-campi.
// - Il frame, una volta caricato, non si riscrive quando la scheda è riletta con morph (React non rileggeva i moduli
//   a ogni load() della pagina): trattieni. Se cambiano i file dei moduli (classe cambiata al passo 3) cambia
//   l'impronta nell'indirizzo del frame: il morph passa e Turbo rilegge il frame.
// - File e parti si aprono e si chiudono (open e aperti di React); quando un'azione riscrive un file, il file e le sue
//   parti aperte restano aperti (fileTargetConnected, unitaTargetConnected).
// - Le azioni di un file (Leggi tutti, Conferma senza dubbi, la copia Word, Dividi, la divisione) vanno con fetch come
//   le api.post di React; la risposta è un turbo-stream (toast, il file riletto, la scheda). Durante la richiesta i
//   pulsanti dell'azione sono disattivati (busy del file: le sue azioni; busy della divisione: Dividi, Conferma, Un
//   modulo solo; mai Annulla). Senza una risposta leggibile il testo generico di formatApiErrorDetail.
// - Un'azione che riscrive il file (Leggi tutti, Conferma senza dubbi, copia Word) non chiude la divisione aperta: l'editor
//   (con le modifiche non salvate) passa nel file nuovo, come lo stato di Divisione restava in React. Solo la
//   divisione salvata la chiude.
// - Allo stesso modo gli editor dei moduli delle parti aperte tengono applicabilità, condizione, «Stesso modulo di»,
//   il campo da aggiungere e l'avviso (ModuloController.istantanea / ripristina): React rifaceva solo il resto da u.
// - L'editor della divisione: righe in ordine di inizio, menu dei titoli senza quelli scelti, «Conferma la
//   divisione» disattivato con un nome di meno di 2 caratteri; «Annulla» lo chiude senza salvare.
export default class extends Controller {
  static targets = ["file", "unita", "editor"]

  initialize() {
    this.fileAperti = new Set()
    this.unitaAperte = new Set()
    this.editoriTenuti = new Map() // doc_id -> editor della divisione del file appena riscritto
    this.moduliTenuti = new Map() // doc_id -> [chiave della parte, istantanea dell'editor del modulo]
  }

  trattieni(event) {
    if (event.target !== this.element || !this.element.hasAttribute("complete")) return
    const nuovo = event.detail?.newElement?.getAttribute("src")
    if (!nuovo || new URL(nuovo, location.href).href === new URL(this.element.getAttribute("src"), location.href).href) event.preventDefault()
  }

  // --- aperture -----------------------------------------------------------------------------------------------

  apriFile(event) {
    const file = event.currentTarget.closest("[data-moduli-campi-target~='file']")
    const id = file.dataset.docId
    this.fileAperti.has(id) ? this.fileAperti.delete(id) : this.fileAperti.add(id)
    this.mostraFile(file)
  }

  apriUnita(event) {
    const unita = event.currentTarget.closest("[data-moduli-campi-target~='unita']")
    const key = unita.dataset.key
    this.unitaAperte.has(key) ? this.unitaAperte.delete(key) : this.unitaAperte.add(key)
    this.mostra(unita, this.unitaAperte.has(key))
  }

  fileTargetConnected(file) {
    this.mostraFile(file)
    const moduli = this.moduliTenuti.get(file.dataset.docId) || []
    this.moduliTenuti.delete(file.dataset.docId)
    moduli.forEach(([key, ist]) => {
      const editor = file.querySelector(`[data-controller~='modulo'][data-modulo-key-value="${CSS.escape(key)}"]`)
      if (!editor) return
      ModuloController.ripristina(editor, ist)
      this.application.getControllerForElementAndIdentifier(editor, "modulo")?.aggiorna()
    })
    const editor = this.editoriTenuti.get(file.dataset.docId)
    this.editoriTenuti.delete(file.dataset.docId)
    if (!editor) return
    file.querySelector(`#${CSS.escape(`divisione-editor-${file.dataset.docId}`)}`)?.appendChild(editor)
  }

  fileTargetDisconnected(file) {
    const moduli = [...file.querySelectorAll("[data-controller~='modulo']")]
      .filter((e) => this.unitaAperte.has(e.dataset.moduloKeyValue))
      .map((e) => [e.dataset.moduloKeyValue, ModuloController.istantanea(e)])
    if (moduli.length) this.moduliTenuti.set(file.dataset.docId, moduli)
    const editor = file.querySelector("[data-moduli-campi-target~='editor']")
    // l'editor della divisione appena salvata (data-in-salvataggio) si chiude con il file
    if (editor && !editor.hasAttribute("data-in-salvataggio")) this.editoriTenuti.set(file.dataset.docId, editor)
  }

  unitaTargetConnected(unita) {
    this.mostra(unita, this.unitaAperte.has(unita.dataset.key))
  }

  mostraFile(file) {
    this.mostra(file, this.fileAperti.has(file.dataset.docId))
  }

  // il corpo, le frecce e il pulsante della testa (il primo figlio) di un file o di una parte
  mostra(el, aperto) {
    const testa = el.firstElementChild
    const corpo = [...el.children].find((c) => c.hasAttribute("data-corpo"))
    if (corpo) corpo.hidden = !aperto
    testa.querySelectorAll(":scope > [data-aperto]").forEach((x) => { x.hidden = !aperto })
    testa.querySelectorAll(":scope > [data-chiuso]").forEach((x) => { x.hidden = aperto })
  }

  // --- azioni del file ----------------------------------------------------------------------------------------

  azione(event) {
    const pulsante = event.currentTarget
    this.invia(pulsante, pulsante.dataset.url, { method: "POST" }, "[data-busy-file]")
  }

  carica(event) {
    const input = event.currentTarget
    const file = input.files?.[0]
    if (!file) return
    const dati = new FormData()
    dati.append("file", file)
    this.invia(input, input.dataset.url, { method: "POST", body: dati }, "[data-busy-file]").finally(() => { input.value = "" })
  }

  dividi(event) {
    const pulsante = event.currentTarget
    this.invia(pulsante, `${this.fileDi(pulsante).dataset.url}/dividi`, { method: "POST" }, "[data-busy-divisione]")
  }

  confermaDivisione(event) {
    const editor = event.currentTarget.closest("[data-moduli-campi-target~='editor']")
    const sezioni = this.righe(editor).map((r) => ({
      inizio: Number(r.dataset.inizio),
      nome: r.querySelector("[data-nome-parte]").value,
      escluso: r.querySelector("[data-escluso-parte]").checked,
    }))
    this.salvaDivisione(event.currentTarget, sezioni)
  }

  unModuloSolo(event) {
    this.salvaDivisione(event.currentTarget, [])
  }

  salvaDivisione(pulsante, sezioni) {
    const editor = pulsante.closest("[data-moduli-campi-target~='editor']")
    editor?.setAttribute("data-in-salvataggio", "")
    this.invia(pulsante, `${this.fileDi(pulsante).dataset.url}/divisione`, {
      method: "PUT", body: JSON.stringify({ sezioni }), headers: { "Content-Type": "application/json" },
    }, "[data-busy-divisione]").then((salvata) => { if (!salvata) editor?.removeAttribute("data-in-salvataggio") })
  }

  fileDi(el) {
    return el.closest("[data-moduli-campi-target~='file']")
  }

  // busy: i pulsanti `occupati` del file disattivati e l'icona che gira durante la richiesta
  async invia(origine, url, { method, body, headers = {} }, occupati) {
    const file = this.fileDi(origine)
    const pulsanti = [...file.querySelectorAll(occupati)].filter((b) => !b.disabled)
    pulsanti.forEach((b) => { b.disabled = true })
    const attesa = origine.closest("label") || origine
    this.attesa(attesa, true)
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const risposta = await fetch(url, {
        method, body, credentials: "same-origin",
        headers: { Accept: "text/vnd.turbo-stream.html", ...headers, ...(token ? { "X-CSRF-Token": token } : {}) },
      })
      // una risposta rediretta (sessione scaduta) o senza turbo-stream non ha un dettaglio da mostrare
      if (risposta.redirected || !(risposta.headers.get("Content-Type") || "").includes("turbo-stream")) throw new Error("risposta")
      Turbo.renderStreamMessage(await risposta.text())
      return risposta.ok
    } catch {
      window.toast?.error("Si è verificato un errore. Riprova.")
      return false
    } finally {
      pulsanti.forEach((b) => { if (b.isConnected) b.disabled = false })
      if (attesa.isConnected) this.attesa(attesa, false)
      this.editorTargets.forEach((e) => this.aggiornaEditor(e))
    }
  }

  attesa(el, si) {
    el.querySelectorAll(":scope > [data-attesa]").forEach((x) => { x.hidden = !si })
    el.querySelectorAll(":scope > [data-pronto]").forEach((x) => { x.hidden = si })
  }

  // --- editor della divisione ---------------------------------------------------------------------------------

  editorTargetConnected(editor) {
    this.riassunto(editor).hidden = true
    this.aggiornaEditor(editor)
  }

  editorTargetDisconnected() {
    this.fileTargets.forEach((file) => {
      const riassunto = file.querySelector("[data-riassunto-divisione]")
      if (riassunto && !file.querySelector("[data-moduli-campi-target~='editor']")) riassunto.hidden = false
    })
  }

  riassunto(editor) {
    return this.fileDi(editor).querySelector("[data-riassunto-divisione]")
  }

  righe(editor) {
    return [...editor.querySelectorAll(":scope > [data-riga-divisione]")]
  }

  annullaDivisione(event) {
    const editor = event.currentTarget.closest("[data-moduli-campi-target~='editor']")
    const riassunto = this.riassunto(editor)
    editor.remove()
    riassunto.hidden = false
  }

  togliParte(event) {
    const editor = event.currentTarget.closest("[data-moduli-campi-target~='editor']")
    event.currentTarget.closest("[data-riga-divisione]").remove()
    this.aggiornaEditor(editor)
  }

  nomeParte(event) {
    this.aggiornaEditor(event.currentTarget.closest("[data-moduli-campi-target~='editor']"))
  }

  aggiungiParte(event) {
    const menu = event.currentTarget
    const editor = menu.closest("[data-moduli-campi-target~='editor']")
    const titolo = JSON.parse(editor.dataset.titoli).find((t) => String(t.riga) === menu.value)
    menu.value = ""
    if (!titolo) return
    const riga = editor.querySelector("template[data-modello-riga]").content.firstElementChild.cloneNode(true)
    riga.dataset.inizio = String(titolo.riga)
    riga.querySelector("[data-numero-riga]").textContent = `riga ${titolo.riga + 1}`
    riga.querySelector("[data-nome-parte]").value = titolo.testo
    const dopo = this.righe(editor).find((r) => Number(r.dataset.inizio) > titolo.riga)
    editor.insertBefore(riga, dopo || editor.querySelector(":scope > [data-comandi-divisione]"))
    this.aggiornaEditor(editor)
  }

  // numeri delle righe, menu dei titoli non scelti, «Conferma la divisione» (busy || un nome < 2 caratteri)
  aggiornaEditor(editor) {
    if (!editor.isConnected) return
    const righe = this.righe(editor)
    righe.forEach((r, i) => { r.dataset.testid = `divisione-riga-${i}` })
    const scelte = new Set(righe.map((r) => Number(r.dataset.inizio)))
    const menu = editor.querySelector("select")
    ;[...menu.options].slice(1).forEach((o) => o.remove())
    JSON.parse(editor.dataset.titoli).filter((t) => !scelte.has(t.riga)).forEach((t) => {
      menu.add(new Option(`riga ${t.riga + 1}: ${t.testo}`, String(t.riga)))
    })
    const conferma = editor.querySelector("[data-conferma-divisione]")
    conferma.disabled = righe.some((r) => r.querySelector("[data-nome-parte]").value.trim().length < 2)
  }
}
